import { isDefined } from '@sgme/fp';
import { HStack, Tooltip, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { clsx } from 'clsx';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  useForwardAccumulatorSelector,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { TimeProgress } from '@/App/tiles/shared/TimeProgress.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton/index.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { forwardAccumulatorApi, useCancelForwardAccumulatorRfsMutation } from '@/store/forwardAccumulator/api/index.ts';
import {
  getForwardAccumulatorQuote,
  getForwardAccumulatorQuoteType,
  getForwardAccumulatorRfsId,
  isPremiumAmountNegative,
  selectForwardAccumulatorRfsStatus,
} from '@/store/forwardAccumulator/selectors.ts';
import type { FxForwardAccumulator } from '@/store/forwardAccumulator/state.ts';
import { requestForwardAccumulatorRfsThunk } from '@/store/forwardAccumulator/thunk.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

import './../../../../../utils/rateComponentStyling.css';

export function PriceButton() {
  return (
    <VStack fullWidth>
      <div className="py-2">
        <PriceButtons />
      </div>
    </VStack>
  );
}

function PriceButtons() {
  const rfsStatus = useForwardAccumulatorSelector((accumulator) =>
    selectForwardAccumulatorRfsStatus(accumulator, true),
  );

  const quoteType = useForwardAccumulatorSelector(getForwardAccumulatorQuoteType);

  const {
    currentUser: { userType },
  } = useUserContext();

  const isInternalSales = userType === 'Internal Sales';

  switch (rfsStatus) {
    case 'NOT_PRICEABLE':
      return <NotPriceable quoteType={quoteType} isInternalSales={isInternalSales} />;

    case 'STREAMING_EXECUTABLE':
      return <Progress />;

    case 'AWAITING_INDICATIVE':
      return <ShortcutExecutable />;

    case 'AWAITING_EXECUTABLE':
      return <AwaitingExecutable />;

    case 'PRICEABLE':
      return <Priceable pricingType="Indicative" forceTradeable={false} />;
    case 'ERROR':
      return <Priceable pricingType="Indicative" forceTradeable={false} />;
    case 'STREAMING_INDICATIVE':
      return <Priceable pricingType="Tradeable" forceTradeable={true} />;

    case 'BYPASSABLE_ERROR':
      return <Bypassable />;
    default:
      return <NotPriceable quoteType={quoteType} isInternalSales={isInternalSales} />;
  }
}

function AwaitingExecutable() {
  const tileId = useForwardAccumulatorTileId();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const clientsWay = useForwardAccumulatorSelector((forwardAccumulator) => {
    return forwardAccumulator.properties.clientsWay;
  });
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const [cancelForwardAccumulatorRfs] = useCancelForwardAccumulatorRfsMutation();

  const rfsId = useForwardAccumulatorSelector(getForwardAccumulatorRfsId);
  const handleCancelRequest = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      'Executable price request cancelled',
      isDefined(rfsId) ? rfsId : 'unknown',
    );

    if (isDefined(rfsId)) {
      cancelForwardAccumulatorRfs({
        tileId,
        rfsId,
      });
    }
  }, [rfsId, tileId, cancelForwardAccumulatorRfs, sgwtWebAnalytics]);

  return (
    <div className="progress" style={{ height: '20px' }}>
      <div
        className={`progress-bar progress-bar-striped progress-bar-animated ${showColoredPrices ? 'rate-colored-prices' : 'bg-info'} ${clientsWay === 'Buy' ? 'buy' : 'sell'} w-100`}
        role="progressbar"
      />
      <button
        type="button"
        className="btn btn-sm btn-flat-primary"
        data-e2e="cancel-rfs-button"
        onClick={handleCancelRequest}
      >
        <i className="icon">close</i>
      </button>
    </div>
  );
}

function Progress() {
  const tileId = useForwardAccumulatorTileId();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const [cancelForwardAccumulatorRfs] = useCancelForwardAccumulatorRfsMutation();

  const clientsWay = useForwardAccumulatorSelector((forwardAccumulator) => {
    return forwardAccumulator.properties.clientsWay;
  });
  const progressData = useForwardAccumulatorSelector(selectProgressData);
  const handleCancelRequest = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      'Executable price request cancelled',
      isDefined(progressData?.rfsId) ? progressData.rfsId : 'unknown',
    );

    if (isDefined(progressData?.rfsId)) {
      cancelForwardAccumulatorRfs({
        tileId,
        rfsId: progressData.rfsId,
      });
    }
  }, [progressData?.rfsId, tileId, cancelForwardAccumulatorRfs, sgwtWebAnalytics]);

  return (
    <TimeProgress
      totalTime={progressData?.totalTime}
      remainingTime={progressData?.remainingTime}
      onCancel={handleCancelRequest}
      data-e2e="rfs"
      clientsWay={clientsWay}
    />
  );
}

function Priceable({
  pricingType,
  forceTradeable,
}: {
  pricingType: 'Tradeable' | 'Indicative';
  forceTradeable: boolean;
}) {
  const dispatch = useAppDispatch();
  const tileId = useForwardAccumulatorTileId();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const clientsWay = useForwardAccumulatorSelector((forwardAccumulator) => {
    return forwardAccumulator.properties.clientsWay;
  });
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const rfsId = useForwardAccumulatorSelector(getForwardAccumulatorRfsId);

  const skippedCreditCheck = useAppSelector((state) => {
    const rfsResponse = forwardAccumulatorApi.endpoints.requestForwardAccumulatorRfs.select({
      requestId: rfsId,
      fixedCacheKey: rfsId,
    })(state);
    return rfsResponse.isSuccess && rfsResponse.data.skipCreditCheck;
  });

  return (
    <button
      data-e2e={`price-button-${pricingType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className={clsx('btn btn-sm btn-block', {
        'btn-sell': showColoredPrices && clientsWay === 'Sell',
        'btn-buy': showColoredPrices && clientsWay === 'Buy',
        'btn-info': !showColoredPrices,
        'btn-warning': skippedCreditCheck,
      })}
      onClick={() => {
        dispatch(requestForwardAccumulatorRfsThunk({ tileId, forceTradeable, skipCreditCheck: skippedCreditCheck }));
        sgwtWebAnalytics?.trackEvent(
          'Forward Accumulator',
          'Price button clicked',
          isDefined(rfsId) ? rfsId : 'unknown',
        );
      }}
    >
      <FormattedMessage id={`request.${pricingType}.price`} />
    </button>
  );
}

function NotPriceable({
  quoteType,
  isInternalSales,
}: {
  quoteType: 'Indicative' | 'Tradeable' | 'Withdraw';
  isInternalSales: boolean;
}) {
  const premiumNegative = useForwardAccumulatorSelector(isPremiumAmountNegative);
  const disallowedMessage = isInternalSales ? 'PRIME_DISALLOWED.SALES' : 'PRIME_DISALLOWED.TRADERS';
  const message = premiumNegative ? disallowedMessage : quoteType;

  return (
    <button
      data-e2e={`price-button-${quoteType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className="btn btn-info btn-sm btn-block"
      disabled
    >
      <FormattedMessage id={`request.${message}.price`} />
    </button>
  );
}

function Bypassable() {
  const dispatch = useAppDispatch();
  const tileId = useForwardAccumulatorTileId();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const quoteType = useForwardAccumulatorSelector(getForwardAccumulatorQuoteType);
  const bypassCheck = true;
  const forceTradeable = quoteType === 'Indicative' || quoteType === 'Withdraw';

  function bypass() {
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      'Bypass price button clicked',
      forceTradeable ? 'tradeable' : 'indicative',
    );
    dispatch(requestForwardAccumulatorRfsThunk({ tileId, forceTradeable: false, skipCreditCheck: true }));
  }
  function skipToExecutable() {
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      'Skip to executable price button clicked',
      forceTradeable ? 'tradeable' : 'indicative',
    );
    dispatch(requestForwardAccumulatorRfsThunk({ tileId, forceTradeable, skipCreditCheck: true }));
  }

  return (
    <HStack fullWidth>
      <button onClick={bypass} type="button" className="btn btn-warning btn-sm btn-block" data-e2e={'button-price'}>
        <FormattedMessage
          id={
            quoteType === 'Indicative'
              ? 'option.pricingButton.bypass.indicative.label'
              : 'option.pricingButton.bypass.executable.label'
          }
        />
      </button>
      <Tooltip contents={<FormattedMessage id="option.pricingButton.shortcutExecutable.tooltip" />}>
        <button
          type="button"
          className={`btn btn-sm btn-icon btn-flat-${bypassCheck ? 'warning' : 'info'}`}
          onClick={skipToExecutable}
          data-e2e={'button-skip-to-executable-price'}
        >
          <i className="icon">skip_next</i>
        </button>
      </Tooltip>
    </HStack>
  );
}

function ShortcutExecutable() {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const clientsWay = useForwardAccumulatorSelector((forwardAccumulator) => {
    return forwardAccumulator.properties.clientsWay;
  });
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);

  const tileId = useForwardAccumulatorTileId();

  const rfsId = useForwardAccumulatorSelector(getForwardAccumulatorRfsId);
  const [cancelForwardAccumulatorRfs] = useCancelForwardAccumulatorRfsMutation();

  const handleCancelRequest = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      'Shortcut to executable price request cancelled',
      isDefined(rfsId) ? rfsId : 'unknown',
    );

    if (isDefined(rfsId)) {
      cancelForwardAccumulatorRfs({
        tileId,
        rfsId,
      });
    }
  }, [rfsId, tileId, cancelForwardAccumulatorRfs, sgwtWebAnalytics]);

  const bypassCheck = false;
  return (
    <div className="progress" style={{ height: '20px' }}>
      <div
        className={`progress-bar progress-bar-striped progress-bar-animated ${showColoredPrices ? 'rate-colored-prices' : 'bg-info'} ${clientsWay === 'Buy' ? 'buy' : 'sell'} w-100`}
        role="progressbar"
      />
      <SgButton
        type="button"
        purpose="discreet"
        size="sm"
        className="btn btn-sm btn-flat-primary flex-grow-0"
        onClick={handleCancelRequest}
      >
        <i className="icon icon-sm">close</i>
      </SgButton>
      <Tooltip contents={<FormattedMessage id="option.pricingButton.shortcutExecutable.tooltip" />}>
        <button
          type="button"
          className={`btn btn-sm btn-icon btn-flat-${bypassCheck ? 'warning' : 'info'}`}
          onClick={() => {
            sgwtWebAnalytics?.trackEvent(
              'Forward Accumulator',
              'Shortcut to executable price button clicked',
              'tradeable',
            );
            dispatch(requestForwardAccumulatorRfsThunk({ tileId, forceTradeable: true }));
          }}
          data-e2e={'skip-to-executable-price-button'}
        >
          <i className="icon">skip_next</i>
        </button>
      </Tooltip>
    </div>
  );
}

function selectProgressData(accumulator: FxForwardAccumulator) {
  const quote = getForwardAccumulatorQuote(accumulator);
  const totalTime =
    accumulator.properties.rfs?.status === 'PRICING' ? accumulator.properties.rfs.initialRfsWindow : undefined;

  return {
    rfsId: getForwardAccumulatorRfsId(accumulator),
    remainingTime: quote?.rfsWindow,
    totalTime,
  };
}
